import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AttendanceTable = ({
  employees,
  onViewEmployeeLog,
  onMarkAttendance,
  selectedPeriod
}) => {
  const [sortConfig, setSortConfig] = useState({ key: 'full_name', direction: 'asc' });

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedEmployees = [...employees].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aValue = a[sortConfig.key] ?? '';
    const bValue = b[sortConfig.key] ?? '';
    if (sortConfig.direction === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const getAttendanceRateBadge = (rate) => {
    if (rate >= 90) {
      return 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20';
    } else if (rate >= 75) {
      return 'bg-amber-500/10 text-amber-600 border-amber-500/20';
    } else {
      return 'bg-rose-500/10 text-rose-600 border-rose-500/20';
    }
  };

  const getInitials = (name) => {
    if (!name) return 'EMP';
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden mb-6">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse min-w-[950px]">
          <thead>
            <tr className="bg-muted/50 border-b border-border text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              <th className="py-3 px-4 cursor-pointer hover:bg-muted/70" onClick={() => handleSort('full_name')}>
                <div className="flex items-center gap-1">
                  Employee
                  <Icon name="ArrowUpDown" size={12} />
                </div>
              </th>
              <th className="py-3 px-4 cursor-pointer hover:bg-muted/70" onClick={() => handleSort('department')}>
                <div className="flex items-center gap-1">
                  Department & Role
                  <Icon name="ArrowUpDown" size={12} />
                </div>
              </th>
              <th className="py-3 px-4 cursor-pointer hover:bg-muted/70 text-center" onClick={() => handleSort('days_present')}>
                <div className="flex items-center justify-center gap-1">
                  Present
                  <Icon name="ArrowUpDown" size={12} />
                </div>
              </th>
              <th className="py-3 px-4 cursor-pointer hover:bg-muted/70 text-center" onClick={() => handleSort('days_late')}>
                <div className="flex items-center justify-center gap-1">
                  Late
                  <Icon name="ArrowUpDown" size={12} />
                </div>
              </th>
              <th className="py-3 px-4 cursor-pointer hover:bg-muted/70 text-center" onClick={() => handleSort('days_absent')}>
                <div className="flex items-center justify-center gap-1">
                  Absent
                  <Icon name="ArrowUpDown" size={12} />
                </div>
              </th>
              <th className="py-3 px-4 cursor-pointer hover:bg-muted/70 text-center" onClick={() => handleSort('work_hours')}>
                <div className="flex items-center justify-center gap-1">
                  Work Hours
                  <Icon name="ArrowUpDown" size={12} />
                </div>
              </th>
              <th className="py-3 px-4 cursor-pointer hover:bg-muted/70" onClick={() => handleSort('attendance_rate')}>
                <div className="flex items-center gap-1">
                  Attendance Rate
                  <Icon name="ArrowUpDown" size={12} />
                </div>
              </th>
              <th className="py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border text-sm">
            {sortedEmployees.length === 0 ? (
              <tr>
                <td colSpan="8" className="py-12 text-center text-muted-foreground">
                  No attendance records found matching filters.
                </td>
              </tr>
            ) : (
              sortedEmployees.map((emp) => (
                <tr key={emp.id} className="hover:bg-muted/30 transition-colors">
                  {/* Employee Name & Code */}
                  <td className="py-3.5 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center text-xs shrink-0 overflow-hidden border border-primary/20">
                        {emp.face_photo_url ? (
                          <img src={emp.face_photo_url} alt={emp.full_name} className="w-full h-full object-cover" />
                        ) : (
                          getInitials(emp.full_name)
                        )}
                      </div>
                      <div>
                        <div className="font-semibold text-foreground">{emp.full_name}</div>
                        <div className="text-xs text-muted-foreground font-mono">{emp.employee_code || 'N/A'}</div>
                      </div>
                    </div>
                  </td>

                  {/* Department & Role */}
                  <td className="py-3.5 px-4">
                    <div className="font-medium text-foreground">{emp.department || 'General'}</div>
                    <div className="text-xs text-muted-foreground">{emp.position || 'Staff'}</div>
                  </td>

                  {/* Present Days */}
                  <td className="py-3.5 px-4 text-center">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-600 border border-emerald-500/20">
                      {emp.days_present} days
                    </span>
                  </td>

                  {/* Late Arrivals */}
                  <td className="py-3.5 px-4 text-center">
                    {emp.days_late > 0 ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-500/10 text-amber-600 border border-amber-500/20">
                        {emp.days_late} late
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">0</span>
                    )}
                  </td>

                  {/* Days Absent */}
                  <td className="py-3.5 px-4 text-center">
                    {emp.days_absent > 0 ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-500/10 text-rose-600 border border-rose-500/20">
                        {emp.days_absent} days
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">0</span>
                    )}
                  </td>

                  {/* Work Hours & Overtime */}
                  <td className="py-3.5 px-4 text-center">
                    <div className="font-semibold text-foreground">{emp.work_hours} hrs</div>
                    {emp.overtime_hours > 0 && (
                      <div className="text-[11px] text-purple-600 font-medium">+{emp.overtime_hours} hrs OT</div>
                    )}
                  </td>

                  {/* Attendance Rate % */}
                  <td className="py-3.5 px-4">
                    <div className="w-36">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="font-medium">{emp.attendance_rate}%</span>
                        <span className={`px-1.5 py-0.2 rounded border text-[10px] font-bold ${getAttendanceRateBadge(emp.attendance_rate)}`}>
                          {emp.attendance_rate >= 90 ? 'Excellent' : emp.attendance_rate >= 75 ? 'Average' : 'Low'}
                        </span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-300 ${
                            emp.attendance_rate >= 90
                              ? 'bg-emerald-500'
                              : emp.attendance_rate >= 75
                              ? 'bg-amber-500'
                              : 'bg-rose-500'
                          }`}
                          style={{ width: `${Math.min(emp.attendance_rate, 100)}%` }}
                        />
                      </div>
                    </div>
                  </td>

                  {/* Actions */}
                  <td className="py-3.5 px-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onViewEmployeeLog(emp)}
                        iconName="Eye"
                        title="View Detailed Log"
                      >
                        Log
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onMarkAttendance(emp)}
                        iconName="CalendarCheck"
                        title="Mark / Edit Attendance"
                      >
                        Punch
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AttendanceTable;
